import { useCallback, useMemo, useState } from 'react';
import {
  delegatedAccountAbi,
  dexAbi,
  useWriteDelegatedAccountWithdrawCollateral,
  useWriteDexWithdrawCollateral,
} from 'sc-interfaces';
import { formatUnits, getAddress, type Hex } from 'viem';
import { useAccount, useConfig } from 'wagmi';
import { useNotify } from '@/feature/notifications';
import { formatTokenAmountDisplay } from '@/utils/tokenNumberUtils.ts';
import { useEnsureChain } from '../../../hooks/useEnsureChain';
import { accountReasonHandler, useAccountErrorHandler } from '../../../hooks/useErrorHandler';
import { useTransactionState } from '../../../hooks/useTransactionMonitor.ts';
import { useWalletNonce } from '../../../hooks/useWalletNonce.ts';
import useAppStore, { useAccountBalance, useActiveExchangeAddress } from '../../../store/rootStore.ts';
import { prepareContractWrite } from '../../../utils/contractWriteHelpers';
import { createRevertDecodingParams } from '../../../utils/decodeScError';
import { telemetry } from '../../../utils/telemetry';

export function useWithdrawProcess() {
  const config = useConfig();
  const notify = useNotify();
  const { address: account } = useAccount();
  const { getNonce } = useWalletNonce();
  const { ensureChain, configChainId } = useEnsureChain();

  const exchangeAddress = useActiveExchangeAddress();
  const token = useAppStore((state) => state.exchange?.token);
  const minWithdraw = useAppStore((state) => state.exchange?.minWithdraw) ?? 0n;
  const accountBalance = useAccountBalance();
  const [operationAmount, setOperationAmount] = useState<bigint>(0n);
  const [transactionData, setTransactionData] = useState<Hex | undefined>(undefined);
  const isDelegated = useAppStore((state) => state.isDelegated);

  const { handleError: handleWithdrawError } = useAccountErrorHandler('withdrawal');

  const maxWithdraw = useMemo(
    () => (token && accountBalance ? { ...token, value: accountBalance } : undefined),
    [accountBalance, token]
  );

  const {
    writeContractAsync: writeWithdrawAsync,
    isPending: isWithdrawPending,
    isError: isWithdrawError,
    isSuccess: isWithdrawSubmitted,
    data: txHash,
    reset: resetWithdraw,
  } = useWriteDexWithdrawCollateral({ config });

  const {
    writeContractAsync: writeDelegatedWithdrawAsync,
    isPending: isDelegatedWithdrawPending,
    isError: isDelegatedWithdrawError,
    isSuccess: isDelegatedWithdrawSubmitted,
    data: delegatedWithdrawTxHash,
    reset: resetDelegatedWithdraw,
  } = useWriteDelegatedAccountWithdrawCollateral({ config });

  const activeWithdrawState = isDelegated
    ? {
        isPending: isDelegatedWithdrawPending,
        isSuccess: isDelegatedWithdrawSubmitted,
        isError: isDelegatedWithdrawError,
        txHash: delegatedWithdrawTxHash,
      }
    : {
        isPending: isWithdrawPending,
        isSuccess: isWithdrawSubmitted,
        isError: isWithdrawError,
        txHash,
      };

  const { currentStatus, setCurrentStatus, isPending, isSuccess } = useTransactionState({
    isWritePending: activeWithdrawState.isPending,
    isWriteSuccess: activeWithdrawState.isSuccess,
    isWriteError: activeWithdrawState.isError,
    txHash: activeWithdrawState.txHash,
    processData: operationAmount,
    messages: {
      confirming: 'Processing your withdrawal. This may take a moment...',
      confirmed: () => {
        if (operationAmount > 0n && token) {
          const amountFormatted = formatTokenAmountDisplay(operationAmount, token);
          const valueUsd = Number(formatUnits(operationAmount, token.decimals));
          telemetry.logWithdrawal(token.symbol, valueUsd);
          return `Withdrawal request sent for processing. ${amountFormatted} ${token.symbol || ''} will be processed within 24 hours.`;
        }
        return 'Withdrawal request sent for processing and will be processed within 24 hours.';
      },
      failed: 'Withdrawal failed. The transaction was reverted by the contract.',
      rejected: 'Withdrawal transaction was rejected. Please try again.',
      error: 'Transaction status could not be determined. Please check your wallet for details.',
    },
    revertDecoding: createRevertDecodingParams(exchangeAddress, transactionData, account),
    reasonHandler: accountReasonHandler,
  });

  const startWithdrawal = useCallback(
    async ({ amount }: { amount: bigint }) => {
      if (!exchangeAddress || !token) {
        notify('Withdrawal configuration is missing. Please try again later.', 'error');
        setCurrentStatus('failed');
        return;
      }
      if (amount <= 0n) {
        notify('Withdrawal amount must be greater than zero.', 'error');
        return;
      }

      try {
        setCurrentStatus('switching_chain');
        await ensureChain();
      } catch (error) {
        handleWithdrawError(error);
        return;
      }

      try {
        setOperationAmount(amount);
        setCurrentStatus('preparing');

        const abi = isDelegated ? delegatedAccountAbi : dexAbi;
        const prepared = await prepareContractWrite({
          config,
          address: exchangeAddress,
          abi,
          functionName: 'withdrawCollateral',
          args: [amount] as const,
          account,
          chainId: configChainId,
        });

        setCurrentStatus('submitting');
        setTransactionData(prepared.transactionData);
        const nonce = await getNonce();
        const writeAsync = isDelegated ? writeDelegatedWithdrawAsync : writeWithdrawAsync;
        await writeAsync({ ...(prepared.writeArgs as any), nonce } as any);
      } catch (error) {
        handleWithdrawError(error);
        setCurrentStatus('failed');
      }
    },
    [
      exchangeAddress,
      token,
      writeWithdrawAsync,
      writeDelegatedWithdrawAsync,
      isDelegated,
      config,
      account,
      setCurrentStatus,
      handleWithdrawError,
      getNonce,
      ensureChain,
      configChainId,
      notify,
    ]
  );

  const resetProcess = useCallback(() => {
    setCurrentStatus('idle');
    setOperationAmount(0n);
    setTransactionData(undefined);
    resetWithdraw();
    resetDelegatedWithdraw();
  }, [resetWithdraw, resetDelegatedWithdraw, setCurrentStatus]);

  return {
    token,
    minWithdraw,
    maxWithdraw,
    startWithdrawal,
    resetProcess,
    currentStatus,
    isPending,
    isSuccess,
  };
}
